import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";

export default function StandingsOverlay() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const transparent = sp.get("bg") === "transparent";
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const [sponsorIdx, setSponsorIdx] = useState(0);

  useEffect(() => {
    const load = () => api.get(`/interclub/public/tournament/${id}`).then((r) => { setData(r.data); setTick((t) => t + 1); }).catch(() => {});
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [id]);

  const sponsorLogos = data
    ? (data.tournament?.sponsor_logos?.length ? data.tournament.sponsor_logos : (data.tournament?.sponsor_logo ? [data.tournament.sponsor_logo] : []))
    : [];
  useEffect(() => {
    if (sponsorLogos.length <= 1) { setSponsorIdx(0); return; }
    const iv = setInterval(() => setSponsorIdx((i) => (i + 1) % sponsorLogos.length), 6000);
    return () => clearInterval(iv);
  }, [sponsorLogos.length]);

  const bg = transparent ? "transparent" : "#0a0a0c";
  if (!data) return <div className="min-h-screen" style={{ background: bg }} data-testid="standings-overlay" />;

  const { tournament: t, teams, host } = data;
  const color = host?.primary_color || "#007AFF";
  const layout = sp.get("layout") || "full";
  const rows = (teams || []).slice(0, layout === "bar" ? 3 : 8);
  const currentSponsor = sponsorLogos.length ? sponsorLogos[sponsorIdx % sponsorLogos.length] : null;

  if (layout === "bar") {
    return (
      <div className="min-h-screen flex items-end" style={{ background: bg, fontFamily: "'Space Grotesk', system-ui, sans-serif" }} data-testid="standings-overlay">
        <div className="w-full flex items-center gap-4 px-6 py-3" style={{ background: "rgba(10,10,12,0.88)", borderTop: `3px solid ${color}` }} data-testid="overlay-bar">
          <span className="uppercase tracking-[0.18em] text-white/70 text-xs whitespace-nowrap flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-red-500" style={{ animation: "pulse 1.4s ease-in-out infinite" }} /> {t?.name}
          </span>
          <div className="flex-1 flex items-center gap-1 overflow-hidden">
            {rows.map((tm, i) => (
              <div key={tm.id} className="flex items-center gap-2 text-white whitespace-nowrap px-2" data-testid={`overlay-bar-row-${i}`}>
                <span className="text-white/40 tabular-nums text-sm">{i + 1}</span>
                <span className="font-semibold">{tm.name}</span>
                <span className="font-bold tabular-nums text-lg" style={{ color }}>{tm.points || 0}</span>
                {i < rows.length - 1 && <span className="text-white/20">·</span>}
              </div>
            ))}
            {rows.length === 0 && <span className="text-white/40 px-2">No standings yet</span>}
          </div>
          {currentSponsor && (
            <div className="flex items-center" data-testid="overlay-sponsor" key={sponsorIdx}>
              <img src={currentSponsor} alt="" className="h-8 max-w-[140px] object-contain" style={{ animation: "sponsorFade 0.6s ease" }} />
            </div>
          )}
          <span className="text-white/35 text-[10px] whitespace-nowrap">SphereHyx</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-10 text-white relative" style={{ background: bg, fontFamily: "'Space Grotesk', system-ui, sans-serif" }} data-testid="standings-overlay">
      {currentSponsor && (
        <div className="absolute top-8 right-8 flex items-center justify-end" data-testid="overlay-sponsor" style={{ filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.55))" }} key={sponsorIdx}>
          <img src={currentSponsor} alt="" className="h-20 max-w-[240px] object-contain" style={{ animation: "sponsorFade 0.6s ease" }} />
        </div>
      )}
      <div className="max-w-xl" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}>
        <div className="flex items-center gap-3 mb-3">
          <span className="h-3 w-3 rounded-full bg-red-500" style={{ animation: "pulse 1.4s ease-in-out infinite" }} />
          <span className="uppercase tracking-[0.28em] text-white/60 text-xs">Live standings</span>
          <span className="text-white/25 text-[10px]" data-testid="overlay-tick">#{tick}</span>
        </div>
        <h1 className="text-4xl font-bold mb-6" style={{ color }} data-testid="overlay-title">{t?.name}</h1>
        <div className="space-y-2" data-testid="overlay-table">
          {rows.map((tm, i) => (
            <div key={tm.id} className="flex items-center gap-4 rounded-xl px-5 py-3 backdrop-blur-sm" style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={`overlay-row-${i}`}>
              <span className="w-6 text-white/45 tabular-nums text-lg">{i + 1}</span>
              <span className="flex-1 font-semibold truncate text-lg">{tm.name}</span>
              <span className="text-white/50 text-sm tabular-nums">{tm.won || 0}-{tm.drawn || 0}-{tm.lost || 0}</span>
              <span className="w-16 text-right font-bold tabular-nums text-xl" style={{ color }}>{tm.points || 0}</span>
            </div>
          ))}
          {rows.length === 0 && <p className="text-white/40">No teams yet.</p>}
        </div>
        <p className="mt-6 text-white/35 text-xs">Auto-refreshing every 15s · Powered by SphereHyx</p>
      </div>
    </div>
  );
}
